import { FC, FormEventHandler, useEffect, useMemo, useState } from 'react'
import InputGroup from 'components/base/forms/InputGroup'
import Button from 'components/base/forms/Button'
import { toast } from 'components/base/Toast'
import { Group } from 'common/types/responses'
import { ErrorMessage } from 'components/base/Messages'
import { preventDefault } from 'common/utils/forms/preventDefault'
import {
  useCreateGroupMutation,
  useUpdateGroupMutation,
} from 'common/services/useGroup'
import AddRemoveMembers from 'components/AddRemoveMembers'

type CreateEditGroupProps = {
  group?: Group
  onComplete?: (group: Group) => void
  onCancel?: () => void
}

const defaultGroup: Partial<Group> = {
  name: '',
  users: [],
}

const CreateEditGroup: FC<CreateEditGroupProps> = ({
  group,
  onCancel,
  onComplete,
}) => {
  const isEdit = !!group
  const [data, setData] = useState<Partial<Group>>(group || defaultGroup)

  useEffect(() => {
    document.getElementById('name')?.focus()
  }, [])

  const handleChange = (field: keyof Group, value: any) => {
    setData((prevData) => ({
      ...prevData,
      [field]: value,
    }))
  }

  const isValid = !!data.name

  const { toAdd, toRemove, toUpdate } = useMemo(() => {
    const toRemove = (group?.users || []).filter(
      (v) => !data?.users?.find((u) => v.user_id === u.user_id),
    )
    const toAdd = data?.users?.filter(
      (v) => !group?.users?.find((u) => v.user_id === u.user_id),
    )
    const toUpdate = data?.users?.filter(
      (v) => group?.users?.find((u) => v.user_id === u.user_id),
    )

    return { toAdd, toRemove, toUpdate }
  }, [group, data])

  const [createGroup, { error: createError, isLoading: creating }] =
    useCreateGroupMutation({})

  const [updateGroup, { error: updateError, isLoading: updating }] =
    useUpdateGroupMutation({})

  const isLoading = updating || creating
  const error = createError || updateError

  const save = () => {
    const func = isEdit ? updateGroup : createGroup

    func({
      ...data,
      toAdd,
      toRemove,
      toUpdate,
      users: data.users,
    } as any).then((res) => {
      if (!('error' in res)) {
        toast('Success', `${group ? 'Updated' : 'Created'} group`)
        onComplete?.(res.data)
      }
    })
  }

  const submit: FormEventHandler = (e) => {
    preventDefault(e)
    if (isValid) {
      save()
    }
  }

  return (
    <form onSubmit={submit}>
      <div className='d-flex flex-column gap-5'>
        <div className='d-flex justify-content-between gap-4 flex-nowrap'>
          <div className='flex-fill'>
            <InputGroup
              autoFocus
              title='Group Name'
              required
              id='name'
              isValid={!!data.name}
              value={data.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>
        </div>
        <AddRemoveMembers
          setGroupMembers={(v) => {
            handleChange(
              'users',
              v.map((v) => ({ ...v, user_id: v.user_id || v.id })),
            )
          }}
          groupMembers={data.users!}
        />
        <ErrorMessage>{error}</ErrorMessage>
        <div className='d-flex justify-content-end gap-2'>
          {onCancel && (
            <Button
              style={{ width: 140 }}
              theme='secondary'
              onClick={onCancel}
              type='button'
              disabled={isLoading}
            >
              Cancel
            </Button>
          )}

          <Button
            style={{ width: 140 }}
            type='submit'
            disabled={!isValid || isLoading}
          >
            {isEdit ? 'Update Group' : 'Create Group'}
          </Button>
        </div>
      </div>
    </form>
  )
}

export default CreateEditGroup
